import { Building2, ChevronRight, LogOut, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthFrame from '../../components/layout/AuthFrame';
import { useAuth, type OrgOption } from '../../contexts/AuthContext';

function OrganisationOption({ option, onSelect }: { option: OrgOption; onSelect: () => void }) {
  const isAdmin = option.membership.role === 'admin';
  return (
    <button type="button" onClick={onSelect} className="app-focus-ring group flex min-h-20 w-full items-center gap-4 border border-app-line bg-app-surface p-4 text-left transition hover:border-app-ink hover:bg-app-surface-raised">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-app-canvas text-app-ink"><Building2 className="h-5 w-5" aria-hidden="true" /></span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-app-ink">{option.organisation.name}</span>
        <span className={`mt-1 inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase ${isAdmin ? 'text-app-positive' : 'text-app-muted'}`}>
          {isAdmin ? <Shield className="h-3 w-3" aria-hidden="true" /> : null}{isAdmin ? 'Administrator' : 'Member'}
        </span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-app-muted transition-transform group-hover:translate-x-0.5 group-hover:text-app-ink" aria-hidden="true" />
    </button>
  );
}

export default function SelectOrg() {
  const { user, allOrganisations, switchOrg, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSelectOrg = (orgId: string) => {
    switchOrg(orgId);
    navigate('/app', { replace: true });
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  if (allOrganisations.length === 0) {
    return (
      <AuthFrame eyebrow="Workspace access" title="No active workspace" description="Your account is not a member of an active organisation. Contact your administrator to request access.">
        <button type="button" onClick={handleSignOut} className="btn-primary gap-2"><LogOut className="h-4 w-4" aria-hidden="true" />Sign out</button>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame eyebrow="Workspace access" title="Select a workspace" description={user?.email ? `Signed in as ${user.email}. Choose the organisation you want to open.` : 'Choose the organisation you want to open.'}>
      <div className="space-y-3">
        {allOrganisations.map(option => <OrganisationOption key={option.organisation.id} option={option} onSelect={() => handleSelectOrg(option.organisation.id)} />)}
      </div>
      <div className="mt-8 border-t border-app-line pt-5">
        <button type="button" onClick={handleSignOut} className="btn-ghost gap-2"><LogOut className="h-4 w-4" aria-hidden="true" />Sign out</button>
      </div>
    </AuthFrame>
  );
}
